import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { StepBar } from "../components/ProgressTimeline";
import { Bath, Search, ShieldCheck, MessageCircle, AlertCircle, ExternalLink, Truck, Phone, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FALLBACK_WELOVE_LOGO = "/welove-default.png";
const FLOWLINE_LOGO = "/flowline.png";

export default function TrackingPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");

  useEffect(() => {
    if (!code) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/track/${encodeURIComponent(code.toUpperCase())}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "We couldn't find a bath with that code. Please double-check and try again." : "Sorry, something went wrong. Please try again later."))
      .finally(() => setLoading(false));
  }, [code]);

  const submit = (e) => {
    e.preventDefault();
    const v = input.trim().toUpperCase();
    if (!v) return;
    navigate(`/track/${v}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top brand bar */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/track" className="flex items-center gap-3">
            <img
              src={data?.merchant_logo_url ? `${BACKEND_URL}${data.merchant_logo_url}` : FALLBACK_WELOVE_LOGO}
              alt="WeLove"
              className="h-9 w-9 rounded-md ring-1 ring-zinc-200 object-cover"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-extrabold tracking-tighter text-base text-zinc-950">WeLove</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Whirlpool order tracking</span>
            </div>
          </Link>
          <div className="text-[11px] text-zinc-500 font-mono hidden sm:block">
            Need help? <a href="mailto:hello@welove.example" className="text-zinc-950 hover:underline">hello@welove.example</a>
          </div>
        </div>
      </header>

      {/* No code — entry screen */}
      {!code && (
        <main className="mx-auto max-w-2xl px-4 sm:px-6 py-16 grid-bg">
          <div className="text-center mb-10">
            <Bath className="h-10 w-10 mx-auto text-zinc-900 mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-zinc-950">
              Track your bath.
            </h1>
            <p className="text-sm text-zinc-600 mt-3 max-w-md mx-auto">
              Enter the tracking code from your order confirmation to see exactly where your whirlpool bath is in production.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">
              Tracking code
            </label>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              placeholder="WB-XXXXXX"
              className="mt-2 h-12 font-mono text-lg tracking-wider"
              data-testid="track-input"
              autoFocus
            />
            <Button
              type="submit"
              className="w-full mt-4 h-12 bg-zinc-950 hover:bg-zinc-800 text-white text-base font-semibold"
              data-testid="track-submit"
            >
              <Search className="h-4 w-4 mr-2" /> Track my order
            </Button>
            <p className="text-[11px] text-zinc-500 mt-4 text-center">
              Your tracking code looks like <span className="font-mono font-semibold text-zinc-700">WB-A4F2D9</span> and is in your order email.
            </p>
          </form>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Trust icon={ShieldCheck} title="Built for you" desc="Every WeLove whirlpool bath is hand-fitted by our specialist team in Manchester." />
            <Trust icon={Bath} title="Six clear stages" desc="From order to delivery, you'll see exactly which stage your bath is at." />
            <Trust icon={MessageCircle} title="Real updates" desc="Tracking refreshes the moment our team updates the record — no waiting around." />
          </div>
        </main>
      )}

      {/* Code provided */}
      {code && (
        <main className="mx-auto max-w-4xl px-4 sm:px-6 py-10 sm:py-14">
          {loading && <div className="text-center text-zinc-400 py-20">Loading your order…</div>}

          {error && (
            <div className="bg-white border border-rose-200 rounded-lg p-8 text-center">
              <AlertCircle className="h-8 w-8 mx-auto text-rose-600 mb-3" />
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-2">Order not found</h2>
              <p className="text-sm text-zinc-600">{error}</p>
              <Link to="/track" className="inline-block mt-6 text-sm font-semibold text-zinc-950 hover:underline">
                Try another code →
              </Link>
            </div>
          )}

          {data && (
            <>
              {/* Header card */}
              <div className="bg-zinc-950 text-white rounded-xl overflow-hidden mb-6 relative">
                <div className="absolute inset-0 grid-bg opacity-10" />
                <div className="relative p-6 sm:p-10">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold mb-3">
                    Order tracking · {data.tracking_code}
                  </div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight max-w-2xl">
                    {data.product}
                  </h1>

                  <div className="mt-6 inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-md px-3 py-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      Currently: {data.current_stage_label}
                    </span>
                  </div>

                  {data.next_eta && (
                    <div className="mt-4 text-sm text-white/80">
                      Expected back in our warehouse:{" "}
                      <span className="font-semibold text-white">
                        {new Date(data.next_eta).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Issue banner */}
              {data.has_issue && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm text-amber-900">Our team is reviewing this order</div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      We've flagged a small query and will be in touch shortly with an update. Nothing for you to do.
                    </div>
                  </div>
                </div>
              )}

              {/* Step bar */}
              <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-10 mb-6 shadow-sm">
                <h2 className="font-heading text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-6 md:mb-8 text-center">
                  Your order's journey
                </h2>
                <StepBar stages={data.stages} hasIssue={data.has_issue} />
              </div>

              {/* Dispatch / delivery info */}
              {data.dispatch && (data.dispatch.consignment_number || data.current_stage === "dispatched") && (
                <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6 shadow-sm" data-testid="dispatch-block">
                  <div className="flex items-start gap-4">
                    <div className="h-11 w-11 rounded-md bg-zinc-950 text-white flex items-center justify-center flex-shrink-0">
                      <Truck className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">
                        Delivery · {data.dispatch.haulier}
                      </div>
                      <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950 mt-1">
                        {data.dispatch.service}
                      </h3>
                      {data.dispatch.expected_delivery_date && (
                        <div className="text-sm text-zinc-700 mt-1">
                          Expected delivery:{" "}
                          <span className="font-semibold">
                            {new Date(data.dispatch.expected_delivery_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                          </span>
                        </div>
                      )}
                      {data.dispatch.consignment_number && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                            Consignment
                          </span>
                          <span className="font-mono text-sm font-semibold text-zinc-950 bg-zinc-100 px-2 py-0.5 rounded">
                            {data.dispatch.consignment_number}
                          </span>
                          {data.dispatch.haulier_tracking_url && (
                            <a
                              href={data.dispatch.haulier_tracking_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-semibold text-zinc-950 hover:underline flex items-center gap-1"
                              data-testid="dispatch-haulier-link"
                            >
                              Track on UPN <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      )}
                      {data.dispatch.haulier_tracking_instructions && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded p-3">
                          <Info className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0 mt-0.5" />
                          <span>{data.dispatch.haulier_tracking_instructions}</span>
                        </div>
                      )}
                      <div className="mt-3 flex items-start gap-2 text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded p-3">
                        <Phone className="h-3.5 w-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
                        <span>{data.dispatch.info_note}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <InfoCard icon={MessageCircle} title="Questions?" body="Get in touch and quote your tracking code so we can pull up your order instantly." cta={{ href: `mailto:hello@welove.example?subject=Order ${data.tracking_code}`, label: "Email our team" }} />
                <InfoCard icon={ShieldCheck} title="Stage updates in real time" body="This page shows live information from our operations system — updated the moment our team works on your bath." />
              </div>

              <div className="mt-8 text-center text-[11px] text-zinc-400">
                Last updated {new Date(data.updated_at).toLocaleString("en-GB")} ·{" "}
                <Link to="/track" className="underline hover:text-zinc-700">Track a different order</Link>
              </div>
            </>
          )}
        </main>
      )}

      <footer className="border-t border-zinc-200 mt-12">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[11px] text-zinc-500">© WeLove · Whirlpool baths, hand-fitted in Manchester.</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-[11px] text-zinc-500">
              <ExternalLink className="h-3 w-3" />
              <a href="mailto:hello@welove.example" className="hover:text-zinc-900">hello@welove.example</a>
            </span>
            <a
              href="https://soubor-apps.com"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-900 transition-colors"
              data-testid="powered-by-flowline"
            >
              <img src={FLOWLINE_LOGO} alt="Flowline" className="h-4 w-4 object-contain" />
              <span>Powered by <span className="font-semibold text-zinc-700">Flowline</span> · Soubor</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Trust = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-zinc-200 rounded-md p-4">
    <Icon className="h-5 w-5 text-zinc-900 mb-2" strokeWidth={1.5} />
    <div className="font-heading font-bold text-sm tracking-tight text-zinc-950">{title}</div>
    <div className="text-xs text-zinc-600 mt-1 leading-relaxed">{desc}</div>
  </div>
);

const InfoCard = ({ icon: Icon, title, body, cta }) => (
  <div className="bg-white border border-zinc-200 rounded-md p-5">
    <Icon className="h-5 w-5 text-zinc-900 mb-2" strokeWidth={1.5} />
    <div className="font-heading font-bold text-sm tracking-tight text-zinc-950">{title}</div>
    <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{body}</p>
    {cta && (
      <a href={cta.href} className="inline-block mt-3 text-xs font-semibold text-zinc-950 hover:underline">
        {cta.label} →
      </a>
    )}
  </div>
);
